import { useMemo, useState } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { format, parseISO, startOfDay, endOfDay, differenceInMinutes, eachDayOfInterval, isSameDay } from 'date-fns';
import { it } from 'date-fns/locale';
import { Clock, X, Info } from 'lucide-react';

export function BookingOccupancyCalendar({ bookings }) {
  const [selectedCalDate, setSelectedCalDate] = useState(null);

  const { bookedLight, bookedMedium, bookedHeavy, bookedFull } = useMemo(() => {
    const light = [], medium = [], heavy = [], full = [];
    const dayMap = new Map();
    bookings.forEach(booking => {
      if (!booking.start_datetime || !booking.end_datetime) return;
      const bStart = parseISO(booking.start_datetime);
      const bEnd = parseISO(booking.end_datetime);
      if (bEnd <= bStart) return;
      const days = eachDayOfInterval({ start: startOfDay(bStart), end: startOfDay(bEnd) });
      days.forEach(day => {
        const dayS = startOfDay(day);
        const dayE = endOfDay(day);
        const overlapStart = bStart > dayS ? bStart : dayS;
        const overlapEnd = bEnd < dayE ? bEnd : dayE;
        if (overlapEnd <= overlapStart) return;
        const mins = differenceInMinutes(overlapEnd, overlapStart);
        const key = format(day, 'yyyy-MM-dd');
        dayMap.set(key, (dayMap.get(key) || 0) + mins);
      });
    });
    dayMap.forEach((totalMins, key) => {
      const fraction = Math.min(totalMins / (24 * 60), 1);
      const date = parseISO(key);
      if (fraction >= 0.95) full.push(date);
      else if (fraction >= 0.67) heavy.push(date);
      else if (fraction >= 0.34) medium.push(date);
      else if (fraction > 0) light.push(date);
    });
    return { bookedLight: light, bookedMedium: medium, bookedHeavy: heavy, bookedFull: full };
  }, [bookings]);

  const bookingsForCalDate = useMemo(() => {
    if (!selectedCalDate) return [];
    return bookings.filter(b => {
      if (!b.start_datetime || !b.end_datetime) return false;
      const bStart = parseISO(b.start_datetime);
      const bEnd = parseISO(b.end_datetime);
      if (bEnd <= bStart) return false;
      const days = eachDayOfInterval({ start: startOfDay(bStart), end: startOfDay(bEnd) });
      return days.some(d => isSameDay(d, selectedCalDate));
    });
  }, [selectedCalDate, bookings]);

  const handleCalDayClick = (day) => {
    if (!day) return;
    setSelectedCalDate(prev => prev && isSameDay(prev, day) ? null : day);
  };

  return (
    <div className="flex flex-col items-center space-y-4" data-testid="booking-occupancy-calendar">
      <Calendar
        mode="single"
        selected={selectedCalDate}
        onSelect={handleCalDayClick}
        locale={it}
        className="rounded-lg border p-4 w-full max-w-md"
        modifiers={{ bookedLight, bookedMedium, bookedHeavy, bookedFull }}
        modifiersStyles={{
          bookedLight: { background: 'linear-gradient(to top, hsl(0 70% 55% / 0.35) 25%, transparent 25%)', fontWeight: 600 },
          bookedMedium: { background: 'linear-gradient(to top, hsl(0 70% 55% / 0.40) 50%, transparent 50%)', fontWeight: 700 },
          bookedHeavy: { background: 'linear-gradient(to top, hsl(0 70% 55% / 0.45) 75%, transparent 75%)', fontWeight: 700, color: 'hsl(0 60% 35%)' },
          bookedFull: { background: 'hsl(0 70% 55% / 0.45)', fontWeight: 700, color: 'hsl(0 60% 25%)', opacity: 0.9 },
        }}
        data-testid="booking-calendar"
      />

      {selectedCalDate && (
        <div className="w-full max-w-md space-y-3" data-testid="selected-day-bookings">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm sm:text-base">
              Prenotazioni del {format(selectedCalDate, 'dd MMMM yyyy', { locale: it })}
            </h3>
            <button onClick={() => setSelectedCalDate(null)} className="p-1 rounded-full hover:bg-muted" data-testid="close-day-bookings">
              <X className="w-4 h-4" />
            </button>
          </div>
          {bookingsForCalDate.length === 0 ? (
            <p className="text-sm text-muted-foreground p-3 rounded-lg bg-muted text-center">Nessuna prenotazione per questa giornata</p>
          ) : (
            bookingsForCalDate.map(b => (
              <div key={b.id} className="p-3 rounded-lg border bg-card space-y-1.5" data-testid={`day-booking-${b.id}`}>
                <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                  <Clock className="w-4 h-4 shrink-0" />
                  <span>{format(parseISO(b.start_datetime), 'HH:mm')} - {format(parseISO(b.end_datetime), 'HH:mm')}</span>
                  {!isSameDay(parseISO(b.start_datetime), parseISO(b.end_datetime)) && (
                    <span className="text-xs text-muted-foreground">({format(parseISO(b.start_datetime), 'dd/MM')} - {format(parseISO(b.end_datetime), 'dd/MM')})</span>
                  )}
                </div>
                {b.user_info?.societa_associazione && <p className="text-xs text-muted-foreground pl-6">{b.user_info.societa_associazione}</p>}
                {b.descrizione_manifestazione && <p className="text-xs text-muted-foreground pl-6">{b.descrizione_manifestazione}</p>}
              </div>
            ))
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-5 text-xs sm:text-sm">
        <div className="flex items-center gap-1.5"><div className="w-5 h-5 rounded border" /><span>Libero</span></div>
        <div className="flex items-center gap-1.5"><div className="w-5 h-5 rounded border" style={{ background: 'linear-gradient(to top, hsl(0 70% 55% / 0.35) 25%, transparent 25%)' }} /><span>Poco</span></div>
        <div className="flex items-center gap-1.5"><div className="w-5 h-5 rounded border" style={{ background: 'linear-gradient(to top, hsl(0 70% 55% / 0.40) 50%, transparent 50%)' }} /><span>Met\u00e0</span></div>
        <div className="flex items-center gap-1.5"><div className="w-5 h-5 rounded border" style={{ background: 'linear-gradient(to top, hsl(0 70% 55% / 0.45) 75%, transparent 75%)' }} /><span>Molto</span></div>
        <div className="flex items-center gap-1.5"><div className="w-5 h-5 rounded border" style={{ background: 'hsl(0 70% 55% / 0.45)' }} /><span>Pieno</span></div>
      </div>
      <div className="flex items-center gap-2 p-2 rounded-lg bg-muted text-xs text-muted-foreground">
        <Info className="w-4 h-4 shrink-0" />
        <span>Il calendario mostra le prenotazioni esistenti. Seleziona le date qui sotto.</span>
      </div>
    </div>
  );
}
